import { SymbolDisplay } from '@src/components/SymbolDisplay';
import {
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  Button,
  ButtonGroup,
  ButtonVariants,
  CopyButton,
  CurrencyIcon,
  FormControlSizes,
  HStack,
  IconName,
  IndicatorBadge,
  IndicatorBadgeVariants,
  IndicatorDot,
  IndicatorDotVariants,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Text,
  Toggle,
  useMixpanel,
  usePortal,
  VStack,
} from '@talos/kyoko';
import type { PreTradeResponseData } from '../../redux/preTradeApiTypes';
import { PreTradeTabType, type OpenScenario } from '../../types';
import { PreTradeKBLinkButton } from '../PreTradeKBLinkButton';
import { PrimeScenarioButton } from '../PrimeScenarioButton';
import { ScenarioChip } from './ScenarioChip';
import { ActionButtonsContainer, ScenarioTabsContainer, TabBar, TabSeparator } from './styles';
import { TabButton } from './TabButton';
import { usePreTradeHeaderSymbols } from './usePreTradeHeaderSymbols';

interface PreTradePanelHeaderProps {
  scenarios: PreTradeResponseData[];
  activeTab: PreTradeTabType;
  activeScenarioIndex: number;
  /** Scenarios that have open tabs (subset of all scenarios) */
  openScenarios: OpenScenario[];
  /** Whether this pre-trade panel is showing the currently active session. */
  isActiveSession?: boolean;
  onTabChange: (tab: PreTradeTabType) => void;
  onScenarioSelect: (index: number) => void;
  /** Close a scenario tab (hides it, doesn't remove the scenario) */
  onTabClose?: (index: number) => void;
  /** Use a scenario to create an order */
  onUseScenario: (index: number, source: MixpanelEventSource) => void;
  /** Export pre-trade report */
  onExportReport: (source: MixpanelEventSource) => void;
  /** Show JSON model for the selected scenario */
  onShowJson: () => void;
  /** Copy JSON model for the selected scenario */
  onCopyJson: () => void;
  /** Whether non-considered fields are visible */
  showNonConsidered: boolean;
  /** Toggle non-considered field visibility */
  onToggleShowNonConsidered: () => void;
}

export function PreTradePanelHeader({
  scenarios,
  activeTab,
  activeScenarioIndex,
  openScenarios,
  isActiveSession,
  onTabChange,
  onScenarioSelect,
  onTabClose,
  onUseScenario,
  onExportReport,
  onShowJson,
  onCopyJson,
  showNonConsidered,
  onToggleShowNonConsidered,
}: PreTradePanelHeaderProps) {
  const mixpanel = useMixpanel();
  const { setPortalRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);
  const { showSymbolIcon, symbolIconBaseCurrency, symbolList, symbolDisplayProps } =
    usePreTradeHeaderSymbols(scenarios);

  return (
    <VStack alignItems="stretch" gap="spacing0">
      {/* Panel Header */}
      <HStack p="spacing16" pb="spacing8" justifyContent="space-between" alignItems="center">
        <HStack gap="spacing12" alignItems="center" color="gray.100">
          {symbolDisplayProps ? (
            <Box data-testid="pre-trade-symbol">
              <SymbolDisplay {...symbolDisplayProps} />
            </Box>
          ) : (
            <>
              {showSymbolIcon && symbolIconBaseCurrency && (
                <CurrencyIcon
                  currency={symbolIconBaseCurrency}
                  size={20}
                  data-testid="pre-trade-symbol-icon-currency"
                />
              )}
              <Text size="fontSizeLg" data-testid="pre-trade-symbol">
                {symbolList}
              </Text>
            </>
          )}
          <HStack gap="spacing8" alignItems="center">
            <IndicatorBadge>
              <Text size="fontSizeSmall">Pre-Trade</Text>
            </IndicatorBadge>
            {isActiveSession && (
              <IndicatorBadge variant={IndicatorBadgeVariants.Positive} data-testid="pre-trade-active-session-badge">
                <HStack gap="spacing4" alignItems="center">
                  <IndicatorDot show variant={IndicatorDotVariants.Positive} size={8} />
                  <Text size="fontSizeSmall">Active Session</Text>
                </HStack>
              </IndicatorBadge>
            )}
          </HStack>
        </HStack>
        <PreTradeKBLinkButton />
      </HStack>

      {/* Tab Bar */}
      <TabBar>
        {/* Fixed tabs */}
        <TabButton
          icon={IconName.Clock}
          label="History"
          isActive={activeTab === PreTradeTabType.History}
          onClick={() => {
            mixpanel.track(MixpanelEvent.ChangePreTradeTabHistory, {
              [MixpanelEventProperty.Source]: MixpanelEventSource.PreTradeHistory,
            });
            onTabChange(PreTradeTabType.History);
          }}
        />
        <TabButton
          icon={IconName.ArrowLeftRight}
          label="Scenarios"
          count={openScenarios.length}
          isActive={activeTab === PreTradeTabType.Scenarios}
          onClick={() => onTabChange(PreTradeTabType.Scenarios)}
        />

        {/* Separator after fixed tabs - only shown when there are open scenarios */}
        {openScenarios.length > 0 && <TabSeparator />}

        {/* Scrollable container for open scenario tabs */}
        {openScenarios.length > 0 && (
          <ScenarioTabsContainer gap="spacing12" alignItems="stretch">
            {openScenarios.map(({ scenario, originalIndex }) => (
              <ScenarioChip
                key={originalIndex}
                scenario={scenario}
                index={originalIndex}
                isActive={activeTab === PreTradeTabType.Details && activeScenarioIndex === originalIndex}
                onSelect={onScenarioSelect}
                // Prevent closing the last open tab — would leave the panel in a broken state
                onClose={openScenarios.length > 1 ? onTabClose : undefined}
              />
            ))}
          </ScenarioTabsContainer>
        )}

        {/* Separator before action buttons - shown when both open tabs and buttons are visible */}
        {openScenarios.length > 0 && activeTab === PreTradeTabType.Details && <TabSeparator />}

        {/* Filter controls - pushed to the right, only visible on history tab */}
        {activeTab === PreTradeTabType.History && (
          <ActionButtonsContainer>
            <Box ref={setPortalRef} />
          </ActionButtonsContainer>
        )}

        {/* Action buttons - pushed to the right, only visible on details tab */}
        {activeTab === PreTradeTabType.Details && (
          <ActionButtonsContainer>
            <Toggle
              size={FormControlSizes.Small}
              checked={showNonConsidered}
              onChange={onToggleShowNonConsidered}
              label="Show All Fields"
            />
            <PrimeScenarioButton
              onClick={() => onUseScenario(activeScenarioIndex, MixpanelEventSource.PreTradeScenarioDetails)}
            />
            {/* Hidden until 3.1.0 patch - export implementation not yet wired (UI-6855) */}
            {false && (
              <Button
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={() => onExportReport(MixpanelEventSource.PreTradeScenarioDetails)}
                startIcon={IconName.DocumentDownload}
              >
                Export Pre-Trade Report
              </Button>
            )}
            <ButtonGroup size={FormControlSizes.Small}>
              <Button
                variant={ButtonVariants.Default}
                onClick={onShowJson}
                startIcon={IconName.Code}
                data-testid="pre-trade-show-json"
              >
                Show JSON
              </Button>
              <CopyButton onClick={onCopyJson} data-testid="pre-trade-copy-json">
                Copy JSON
              </CopyButton>
            </ButtonGroup>
          </ActionButtonsContainer>
        )}
      </TabBar>
    </VStack>
  );
}
